import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, CalendarCheck, Flame, MapPin } from "lucide-react";
import { api, formatApiError, formatData, formatMese } from "@/lib/api";
import { Logo } from "@/components/Logo";

const Metric = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <Icon className="h-5 w-5 text-[#1e3a8a]" />
    <p data-testid={testid} className="font-display mt-3 text-3xl font-bold text-[#0f172a]">{value}</p>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
  </div>
);

export default function PassportPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/passport/${token}`).then((r) => setData(r.data)).catch((err) => setError(formatApiError(err)));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="border-b border-slate-200 bg-white"><div className="mx-auto max-w-4xl px-4 py-4 sm:px-6"><Logo /></div></header>
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {!data && !error && <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#1e3a8a]" /></div>}
        {error && <p data-testid="passport-error" className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-800">{error}</p>}
        {data && (
          <div className="fade-up" data-testid="passport-public">
            <div className="relative overflow-hidden rounded-3xl bg-[#172554] p-8 text-white grain sm:p-10">
              <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <span className="eyebrow-label !text-[#93c5fd]"><ShieldCheck className="h-4 w-4" />Passaporto dell'Inquilino</span>
                  <h1 data-testid="passport-tenant-name" className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">{data.nome} {data.cognome_iniziale}</h1>
                  <p className="mt-2 flex items-center gap-2 text-sm text-slate-300">{data.citta && <><MapPin className="h-4 w-4" />{data.citta} · </>}Certificato da {data.locatore} · emesso il {formatData(data.emesso_il)}</p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xs uppercase tracking-wider text-slate-300">Score affidabilità</p>
                  <p data-testid="passport-score" className="font-display text-6xl font-extrabold">{data.score ?? "—"}<span className="text-2xl text-slate-400">/100</span></p>
                </div>
              </div>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <Metric icon={CalendarCheck} label="Mesi registrati" value={data.mesi_registrati} testid="passport-mesi-registrati" />
              <Metric icon={ShieldCheck} label="Pagamenti puntuali" value={data.mesi_puntuali} testid="passport-mesi-puntuali" />
              <Metric icon={Flame} label="Mesi consecutivi puntuali" value={data.mesi_consecutivi} testid="passport-mesi-consecutivi" />
            </div>
            <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-[#0f172a]">Storico pagamenti</h2>
              {data.storico.length === 0 ? <p className="mt-4 text-sm text-slate-500">Nessun pagamento ancora registrato.</p> : (
                <ul className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {data.storico.map((h) => (
                    <li key={h.mese} className={`rounded-xl border px-3 py-2.5 text-sm ${h.puntuale ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
                      <p className="font-semibold capitalize">{formatMese(h.mese)}</p>
                      <p className="text-xs">{h.puntuale ? "Puntuale" : "In ritardo"}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <p className="mt-6 text-center text-xs text-slate-400">Dati condivisi con il consenso dell'inquilino, nel rispetto del Regolamento UE 2016/679.</p>
          </div>
        )}
      </main>
    </div>
  );
}
